<template>
  <dialog-scroll-lock content-class="fill-height" width="500" :value="true" @input="$emit('close')">

    <error-dialog :status="toggleStatus" />

    <v-card class="fill-height d-flex flex-column">
      <v-card-title>
        Ellacard contacts
        <v-spacer />
        <v-btn class="mr-n3" icon @click="$emit('close')">
          <v-icon v-html="mdiClose" />
        </v-btn>
      </v-card-title>
      <v-card-subtitle>
        <router-link :to="{ name: 'account-contacts' }">Manage contacts<v-icon small v-html="mdiChevronRight" /></router-link>
      </v-card-subtitle>
      <div class="flex-grow-0 d-flex align-center">
        <v-checkbox
          v-if="!single"
          class="mt-n1 ml-4"
          hide-details readonly
          :indeterminate="!!(nRowsSelected && (nRowsSelected < rows.length))"
          :input-value="!!nRowsSelected"
          @click="toggleAll"
        />
        <contacts-search content-class="ma-2" :types="types" @search="v => search = v" />
      </div>
      <contacts-table class="flex-grow-1 overflow-auto" :search="search" :types.sync="types" :rows.sync="rows">
        <template v-slot:item-prefix="{ item }">
          <v-checkbox
            v-if="!single"
            class="mt-0 mr-3"
            hide-details readonly
            :indeterminate="!!getSelected(item.contact).indeterminate"
            :input-value="!!getSelected(item.contact).n"
            @click.stop="toggle(item.contact)"
          />
        </template>
        <template v-slot:item-targets="{ item }">
          <div class="ma-n1">
            <v-chip
              v-if="item.contact.email"
              class="ma-1" small
              :color="getSelected(item.contact).email ? 'primary' : null"
              :disabled="!!item.contact.email && forceSelectedKeys.has(item.contact.email.toLowerCase())"
              @click.stop="toggleKey(item.contact, 'email')"
            >
              {{item.contact.email}}
            </v-chip>
            <v-chip
              v-if="item.contact.phone"
              class="ma-1" small
              :color="getSelected(item.contact).phone ? 'primary' : null"
              :disabled="forceSelectedKeys.has(item.contact.phone)"
              @click.stop="toggleKey(item.contact, 'phone')"
            >
              {{item.contact.phone | phone}}
            </v-chip>
          </div>
        </template>
      </contacts-table>
      <v-divider />
      <v-card-actions class="flex-grow-0">
        <v-spacer />
        <v-btn @click="$emit('close')">Close</v-btn>
      </v-card-actions>
    </v-card>
  </dialog-scroll-lock>
</template>


<script>
import ContactsSearch from '@/components/ContactsSearch.vue';
import ContactsTable from '@/components/ContactsTable.vue';
import { mdiClose, mdiChevronRight } from '@mdi/js';


export default {
  name: 'contacts-dialog',

  components: {
    ContactsSearch,
    ContactsTable
  },

  props: {
    target: String, // Descriptor for the input list, either 'recipient' or 'contributor'
    single: Boolean,
    maxN: { type: Number, default: 500 },
    value: { type: Array, default: () => [] },
    force: Function
  },

  data() {
    return {
      search: { groups: [], mode: 'AND', filters: [] },
      rows: [],
      types: {},
      toggleStatus: this.$track('toggle'),
      toggleAllStatus: this.$track('toggleAll')
    };
  },

  computed: {
    targetKeys() {
      const keys = new Set();
      for (const g of this.value)
        for (const t of g.targets)
          keys.add(t.toLowerCase());
      return keys;
    },

    nRowsSelected() {
      let n = 0;
      for (const row of this.rows)
        if (this.getSelected(row.contact).n)
          n++;
      return n;
    },

    forceSelectedKeys() {
      const keys = new Set();
      if (this.force)
        for (const target of this.value)
          if (this.force(target))
            keys.add(target.email?.toLowerCase() || target.phone);
      return keys;
    }
  },

  created() {
    Object.assign(this, { mdiClose, mdiChevronRight });
  },

  methods: {
    getSelected(contact) {
      const selected = {};
      let nSet = 0;
      if (contact.email) {
        nSet++;
        if (this.targetKeys.has(contact.email.toLowerCase()))
          selected.email = true;
      }
      if (contact.phone) {
        nSet++;
        if (this.targetKeys.has(contact.phone.toLowerCase()))
          selected.phone = true;
      }
      selected.n = Object.keys(selected).length;
      selected.indeterminate = selected.n && (selected.n != nSet);
      return selected;
    },

    async toggle(contact) {
      const selected = this.getSelected(contact);
      if (selected.n) {
        const remove = new Set([ 'email', 'phone' ].filter(x => selected[x]).map(x => contact[x].toLowerCase()).filter(x => !this.forceSelectedKeys.has(x)));
        this.$emit('input', this.value.filter(x => !remove.has(x.email?.toLowerCase() || x.phone)));
      } else {
        if (this.value.length >= this.maxN)
          throw new VisibleError(`Too many ${this.target}s`);
        for (const key of [ 'email', 'phone' ]) {
          if (contact[key]) {
            this.$emit('input', [ ...this.value, { to: contact.name || '', [key]: contact[key] } ]);
            break;
          }
        }
      }
    },

    async toggleKey(contact, key) {
      this.$emit('select', contact[key]);
      const selected = this.getSelected(contact);
      if (selected[key]) {
        const remove = contact[key].toLowerCase();
        this.$emit('input', this.value.filter(x => !x[key] || (x[key].toLowerCase() != remove)));
      } else {
        if (this.value.length >= this.maxN)
          throw new VisibleError(`Too many ${this.target}s`);
        this.$emit('input', [ ...this.value, { to: contact.name || '', [key]: contact[key] } ]);
      }
    },

    async toggleAll() {
      const nToAdd = this.rows.length - this.nRowsSelected;
      if (nToAdd) {
        if (this.value.length + nToAdd > this.maxN)
          throw new VisibleError(`Too many ${this.target}s`);
        const toAdd = [];
        for (const row of this.rows) {
          if (!this.getSelected(row.contact).n) {
            for (const key of [ 'email', 'phone' ]) {
              if (row.contact[key]) {
                toAdd.push({ to: row.contact.name || '', [key]: row.contact[key] });
                break;
              }
            }
          }
        }
        this.$emit('input', [ ...this.value, ...toAdd ]);
      } else {
        const toRemove = new Set();
        for (const row of this.rows)
          for (const key of [ 'email', 'phone' ])
            if (row.contact[key] && !this.forceSelectedKeys.has(row.contact[key].toLowerCase()))
              toRemove.add(row.contact[key].toLowerCase());
        this.$emit('input', this.value.filter(x => !toRemove.has(x.email?.toLowerCase() || x.phone)));
      }
    }
  }
}
</script>
